import { useEffect, useRef, useState } from "react";

/**
 * Compact, premium price card for the plumber landing hero.
 * No delivery-time badge, no money-back seal, no scarcity claims.
 * Only subtle entrance animation + count-up on the price.
 */
export default function HeroPriceCard() {
  const [display, setDisplay] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const startedRef = useRef(false);
  const TARGET = 13000;

  useEffect(() => {
    if (!rootRef.current) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setDisplay(TARGET);
      return;
    }
    const el = rootRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            const duration = 1200;
            const t0 = performance.now();
            const step = (t: number) => {
              const p = Math.min(1, (t - t0) / duration);
              const eased = 1 - Math.pow(1 - p, 3);
              setDisplay(Math.round(eased * TARGET));
              if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
            io.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const formatted = display.toLocaleString("tr-TR");

  return (
    <div ref={rootRef} className="hpc-root relative">
      <style>{`
        @keyframes hpc-fade-up {
          0% { transform: translateY(10px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }
        .hpc-card {
          position: relative;
          animation: hpc-fade-up .5s cubic-bezier(.22,.9,.3,1) both;
        }
        .hpc-card-border {
          position: absolute; inset: 0; border-radius: 20px; padding: 1.5px;
          background: linear-gradient(135deg, #149FE8 0%, #16C7B7 50%, #0B3B5A 100%);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor; mask-composite: exclude;
          pointer-events: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .hpc-card { animation: none !important; }
        }
      `}</style>

      <div className="hpc-card rounded-[20px] bg-white p-4 sm:p-5 shadow-[0_14px_34px_-14px_rgba(20,159,232,0.35)]">
        <span className="hpc-card-border" aria-hidden="true" />

        <div className="text-[10.5px] font-extrabold tracking-[0.16em] uppercase text-[var(--ts-blue-dark)]">
          Her Şey Dahil Net Fiyat
        </div>
        <div className="mt-1 text-[12px] font-extrabold tracking-[0.14em] uppercase text-[var(--ts-navy)]">
          Tesisatçılara Özel Tek Paket
        </div>

        <div className="mt-2 flex items-baseline gap-1.5">
          <span
            className="text-[34px] sm:text-[38px] font-extrabold leading-none tabular-nums text-[var(--ts-navy)]"
            aria-label="13.000 TL"
          >
            {formatted}
          </span>
          <span className="text-[18px] sm:text-[20px] font-extrabold text-[var(--ts-navy)]">TL</span>
        </div>

        <div className="mt-2 text-[13px] font-semibold text-[var(--ts-navy)] leading-snug">
          5 Sayfa + Domain + 1 Yıl Hosting + SSL + Kurulum
        </div>

        <div className="mt-2.5 text-[12px] text-[var(--ts-text-2)] leading-snug">
          Firma bilgileriniz ve içerikleriniz eksiksiz iletildikten sonra web siteniz aynı gün içinde yayınlanır ve tarafınıza sunulur.
        </div>

        <div className="mt-2 text-[11px] text-[var(--ts-text-2)] italic leading-snug">
          Bu teklif web sitesi tasarımı, kurulumu ve yayına alma hizmetini kapsar.
        </div>
      </div>
    </div>
  );
}
